/**
 * API Data Transformation Utilities
 *
 * Transforms Election Grid API responses into UI-friendly data structures
 * for the hierarchical election table.
 */

import type {
  ForecastTraderElectionGridResponse,
  ForecastTraderElectionGridProduct,
  ForecastTraderElectionStateGridResponse,
} from '@/types/forecasttrader.types';
import type {
  ElectionContractFromAPI,
  ElectionCandidate,
  CandidateTableRow,
  USStateCode,
  PartyCode,
} from '../types/election.types';
import { STATE_NAMES, isUSStateCode } from '../types/election.types';

/**
 * Maps full state names from API to state codes
 * Example: "Arizona" -> "AZ"
 */
function mapStateNameToCode(stateName: string): USStateCode {
  const normalized = stateName.trim();

  // Find state code by matching state name
  for (const [code, name] of Object.entries(STATE_NAMES)) {
    if (name === normalized) {
      return code as USStateCode;
    }
  }

  // If no match found, try to extract state code from common patterns
  // e.g., "Arizona (AZ)" -> "AZ"
  const codeMatch = normalized.match(/\(([A-Z]{2})\)$/);
  if (codeMatch && isUSStateCode(codeMatch[1])) {
    return codeMatch[1];
  }

  // Default fallback - this should not happen with valid API data
  console.warn(`Could not map state name "${stateName}" to state code`);
  return 'AL'; // Fallback to Alabama
}

/**
 * Determines race type from office string
 */
function mapOfficeToRaceType(office: string): 'Governor' | 'Senate' | 'House' {
  const normalized = office.toLowerCase();

  switch (normalized) {
    case 'governor':
      return 'Governor';
    case 'senate':
      return 'Senate';
    case 'house':
      return 'House';
    default:
      console.warn(`Unknown office type: ${office}`);
      return 'Senate'; // Default fallback
  }
}

/**
 * Parses strike name and party from strike_label
 * Examples:
 * - "Republican" -> { name: "Republican", party: "GOP" } (general election party-based strike)
 * - "Democrat" -> { name: "Democrat", party: "DEM" } (general election party-based strike)
 * - "John Doe (Republican)" -> { name: "John Doe", party: "GOP" } (legacy named-candidate format)
 */

export function parseStrikeLabelWithAffiliation(
  strike_label: string,
  party_affiliation?: string,
): {
  name: string;
  party: PartyCode;
  title: string;
  partyAffiliation: string;
} {
  const normalized = strike_label.trim();

  // First try to parse party from strike_label
  const partyMatch = normalized.match(/^(.+?)\s*\((.+?)\)$/);
  if (partyMatch) {
    const name = partyMatch[1].trim();
    const partyText = partyMatch[2].trim();
    const partyTextLower = partyText.toLowerCase();

    let party: PartyCode = 'IND';
    let title = partyText; // Preserve original casing for minor parties

    if (partyTextLower.includes('republican') || partyTextLower.includes('gop')) {
      party = 'GOP';
      title = 'Republican';
    } else if (partyTextLower.includes('democrat') || partyTextLower.includes('dem')) {
      party = 'DEM';
      title = 'Democrat';
    } else if (partyTextLower.includes('green') || partyTextLower.includes('grn')) {
      party = 'GRN';
      title = 'Green';
    } else if (partyTextLower.includes('independent') || partyTextLower.includes('ind')) {
      party = 'IND';
      title = 'Independent';
    } else if (partyTextLower.includes('nonpartisan')) {
      party = 'IND';
      title = 'Nonpartisan';
    } else if (partyTextLower.includes('unknown')) {
      party = 'IND';
      title = 'Independent';
    }

    // Use party_affiliation from API if available; fall back to title when absent or "unknown"
    const affiliationIsUnknown = !party_affiliation || party_affiliation.toLowerCase().includes('unknown');
    const partyAffiliation = affiliationIsUnknown ? title : party_affiliation;

    return { name, party, title, partyAffiliation };
  }

  // If no party in parentheses, use party_affiliation from API
  let party: PartyCode = 'IND'; // Default when affiliation is absent
  let title = 'Independent';

  if (party_affiliation) {
    const affiliationLower = party_affiliation.toLowerCase();
    if (affiliationLower.includes('republican') || affiliationLower.includes('gop')) {
      party = 'GOP';
      title = 'Republican';
    } else if (affiliationLower.includes('democrat') || affiliationLower.includes('dem')) {
      party = 'DEM';
      title = 'Democrat';
    } else if (affiliationLower.includes('green') || affiliationLower.includes('grn')) {
      party = 'GRN';
      title = 'Green';
    } else if (affiliationLower.includes('independent') || affiliationLower.includes('ind')) {
      party = 'IND';
      title = 'Independent';
    } else if (affiliationLower.includes('nonpartisan')) {
      party = 'IND';
      title = 'Nonpartisan';
    } else if (affiliationLower.includes('unknown') || !party_affiliation.trim()) {
      party = 'IND';
      title = 'Independent';
    } else {
      // Minor party — treat as IND for styling, preserve full name
      party = 'IND';
      title = party_affiliation;
    }
  }

  return {
    name: normalized,
    party,
    title,
    partyAffiliation: party_affiliation || title,
  };
}

/**
 * Creates a race ID for grouping candidates
 * Uses productConid to ensure each product is its own group, even if same state/race type
 */
function createRaceId(
  productConid: number,
  stateCode: USStateCode,
  raceType: 'Governor' | 'Senate' | 'House',
  district?: string,
): string {
  // Use productConid as primary identifier to ensure unique grouping per product
  return `${productConid}-${stateCode}-${raceType}${district ? `-${district}` : ''}`;
}

/**
 * Gets placeholder photo URL for candidate
 * TODO: Replace with actual photo service integration
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getCandidatePhotoUrl(_candidateName: string): string {
  // Placeholder implementation
  return '/api/placeholder/32/32';
}

/**
 * Map API party string ('Democrat', 'Republican', etc.) to internal PartyCode.
 * Returns undefined when input is absent (toss-up).
 */
function mapPartyToCode(party: string | undefined): PartyCode | undefined {
  if (!party) return undefined;
  const lower = party.toLowerCase();
  if (lower.includes('democrat')) return 'DEM';
  if (lower.includes('republican')) return 'GOP';
  if (lower.includes('green')) return 'GRN';
  if (lower.includes('independent') || lower.includes('nonpartisan')) return 'IND';
  return undefined;
}

/**
 * Transforms a single Election Grid API product into ElectionContractFromAPI
 */
export function transformApiProductToContract(
  product: ForecastTraderElectionGridProduct,
  office: 'governor' | 'senate' | 'house',
): ElectionContractFromAPI {
  const stateCode = mapStateNameToCode(product.state);
  const raceType = mapOfficeToRaceType(office);
  const name = product.name;

  return {
    productConid: product.conid,
    state: product.state,
    stateCode,
    office,
    contracts: product.contracts,
    name,
    raceType,
    favorite: mapPartyToCode(product.party),
    favoriteColor: product.color,
    leadingCandidateConid: product.leading_candidate_conid,
    leadingDemConid: product.leading_dem_conid,
    leadingRepConid: product.leading_rep_conid,
    lastTradeTime: product.last_trade_time,
    measuredPeriod: product.measured_period,
    // These will be populated later from MD API or derived
    price: undefined,
    volume: undefined,
    openInterest: undefined,
    tradingCloses: undefined,
  };
}

/**
 * Transforms Election Grid API response to array of ElectionContractFromAPI
 */
export function transformApiResponseToContracts(
  response: ForecastTraderElectionGridResponse,
  office: 'governor' | 'senate' | 'house',
): ElectionContractFromAPI[] {
  return response.products.map((product) => transformApiProductToContract(product, office));
}

/**
 * Transforms a single contract into ElectionCandidate objects
 * Each API contract generates one candidate per strike_label
 */
export function transformContractToCandidates(apiContract: ElectionContractFromAPI): ElectionCandidate[] {
  const candidates: ElectionCandidate[] = [];
  const raceId = createRaceId(
    apiContract.productConid,
    apiContract.stateCode,
    apiContract.raceType,
    apiContract.district,
  );

  for (const contract of apiContract.contracts) {
    const parsed = parseStrikeLabelWithAffiliation(contract.strike_label, contract.party_affiliation);

    const candidate: ElectionCandidate = {
      // Candidate identification
      name: parsed.name,
      title: parsed.title,
      party: parsed.party,
      partyAffiliation: parsed.partyAffiliation,
      photo: getCandidatePhotoUrl(parsed.name),

      // Trading information
      conid_yes: contract.conid_yes,
      conid_no: contract.conid_no,
      exchange: contract.exchange,
      strike_label: contract.strike_label,
      matched: contract.matched,

      // Market data (will be populated from MD API)
      price: 0, // Placeholder - to be updated with real market data
      volume: '0',
      openInterest: 0,

      // Race context
      raceId,
      stateCode: apiContract.stateCode,
      raceName: apiContract.name,
      raceType: apiContract.raceType,
      district: apiContract.district,

      // API-provided leader and timing
      raceFavorite: apiContract.favorite,
      raceColor: apiContract.favoriteColor,
      leadingCandidateConid: apiContract.leadingCandidateConid,
      leadingDemConid: apiContract.leadingDemConid,
      leadingRepConid: apiContract.leadingRepConid,
      lastTradeTime: apiContract.lastTradeTime!,
      measuredPeriod: apiContract.measuredPeriod,

      // Additional metadata
      isIncumbent: false, // TODO: Determine from API data when available
      isLoadingPrice: true, // Will be true until MD API populates prices

      // Store productConid for market-level API calls
      productConid: apiContract.productConid,
    };

    candidates.push(candidate);
  }

  return candidates;
}

/**
 * Transforms ElectionCandidate to CandidateTableRow for hierarchical table
 */
export function transformCandidateToTableRow(candidate: ElectionCandidate): CandidateTableRow {
  return {
    // Race info (for grouping)
    raceId: candidate.raceId,
    productConid: candidate.productConid,
    stateCode: candidate.stateCode,
    raceName: candidate.raceName,
    raceType: candidate.raceType,
    district: candidate.district,
    racePrice: candidate.price, // Individual candidate's price for now
    raceFavorite: candidate.raceFavorite,
    raceColor: candidate.raceColor,
    leadingCandidateConid: candidate.leadingCandidateConid,
    leadingDemConid: candidate.leadingDemConid,
    leadingRepConid: candidate.leadingRepConid,
    lastTradeTime: candidate.lastTradeTime,
    measuredPeriod: candidate.measuredPeriod,

    // Candidate info
    candidateName: candidate.name,
    candidateTitle: candidate.title,
    candidatePhoto: candidate.photo,
    party: candidate.party,
    partyAffiliation: candidate.partyAffiliation,
    price: candidate.price,
    volume: candidate.volume || '0',
    openInterest: candidate.openInterest || 0,

    // Trading contract IDs for bid-ask price fetching
    conid_yes: candidate.conid_yes,
    conid_no: candidate.conid_no,
    exchange: candidate.exchange,
    matched: candidate.matched,
  };
}

/**
 * Full transformation pipeline: API Response -> Hierarchical Table Data
 */
export function transformApiResponseToTableRows(
  response: ForecastTraderElectionGridResponse,
  office: 'governor' | 'senate' | 'house',
): CandidateTableRow[] {
  // Step 1: Transform API response to contracts
  const apiContracts = transformApiResponseToContracts(response, office);

  // Step 2: Transform each contract to candidates
  const allCandidates: ElectionCandidate[] = [];
  for (const contract of apiContracts) {
    const candidates = transformContractToCandidates(contract);
    allCandidates.push(...candidates);
  }

  // Step 3: Transform candidates to table rows
  return allCandidates.map(transformCandidateToTableRow);
}

/**
 * Derives office type from product name.
 * Product names follow pattern: "Arizona Senate", "Florida Governor", "Texas House"
 */
function deriveOfficeFromProductName(productName: string): 'governor' | 'senate' | 'house' {
  const lower = productName.toLowerCase();
  if (lower.includes('governor') || lower.includes('gubernatorial')) return 'governor';
  if (lower.includes('house') || lower.includes('representative')) return 'house';
  // Default to senate
  return 'senate';
}

/**
 * State group structure for "All By State" view
 */
export interface StateGroup {
  stateName: string;
  stateCode: USStateCode;
  rows: CandidateTableRow[];
}

/**
 * Transforms Election State Grid API response into state-grouped table rows.
 * Each state contains products (races) which are transformed using the existing pipeline.
 */
export function transformStateGridResponseToTableRows(response: ForecastTraderElectionStateGridResponse): StateGroup[] {
  return response.states.map((state) => {
    const stateCode = mapStateNameToCode(state.name);

    const rows = state.products.flatMap((product) => {
      const office = deriveOfficeFromProductName(product.name);
      const apiContract = transformApiProductToContract(product, office);
      const candidates = transformContractToCandidates(apiContract);
      return candidates.map(transformCandidateToTableRow);
    });

    return {
      stateName: state.name,
      stateCode,
      rows,
    };
  });
}

/**
 * Utility to update candidate prices from MD API data
 * This would be called after getting market data for the contract IDs
 */
export function updateCandidatePricesFromMD(
  candidates: ElectionCandidate[],
  marketData: Record<number, { price: number; volume?: string; openInterest?: number }>,
): ElectionCandidate[] {
  return candidates.map((candidate) => {
    // Check for price data using yes contract ID (primary trading instrument)
    const priceData = marketData[candidate.conid_yes];

    if (priceData) {
      return {
        ...candidate,
        price: priceData.price,
        volume: priceData.volume || candidate.volume,
        openInterest: priceData.openInterest || candidate.openInterest,
        isLoadingPrice: false,
      };
    }

    return candidate;
  });
}
